<template>
  <div class="flex flex-col my-5 md:mt-12">
    <h2 class="text-center text-4xl md:text-6xl py-3">Sign Up</h2>
    <p class="text-center py-1">
      Already have an account?
      <router-link class="text-brand-green ml-1" :to="{ name: 'home_login' }">Log in</router-link>
    </p>
  </div>

  <div class="flex md:hidden w-1/3 mx-auto border border-brand-blue my-3"></div>

  <div
    v-if="showReferrerToast"
    class="bg-brand-background-light-green border-2 border-brand-green rounded py-3 px-5 my-5 mx-2 md:mx-auto md:max-w-screen-sm flex flex-row"
  >
    <div class="flex-1">
      <h5 class="text-xl md:text-3xl pb-2">
        <span :class="[getCurrentReferrer.landlord_name ? 'capitalize' : '']">{{
          getCurrentReferrer.landlord_name || getCurrentReferrer.username
        }}</span>
        invited you to Rent Application!
      </h5>
      <p class="text-sm">
        Reduce risk and find high quality tenants with Rent Application's optimized process
      </p>
    </div>
    <div class="flex flex-4 justify-end items-start cursor-pointer" @click="closeReferrerToast">
      <XIcon class="text-brand-dark-gray w-5 h-5" />
    </div>
  </div>

  <div
    v-if="applyAppSlug"
    class="border border-brand-red rounded-sm bg-brand-background-light-red md:max-w-screen-sm px-3 py-2 mx-2 md:mx-auto"
  >
    <p class="text-brand-red">
      Looks like you are an applicant. Click
      <router-link
        :to="{ name: 'apply_form', params: { slug: applyAppSlug } }"
        class="text-brand-green cursor-pointer hover:underline"
        >here</router-link
      >
      to go back to your application
    </p>
  </div>

  <div class="flex flex-col md:flex-row md:pt-10 md:max-w-screen-lg mx-auto px-2">
    <div class="flex flex-col items-center justify-center md:w-1/2 px-3 md:px-10 mb-5 md:mb-0">
      <h2 class="text-3xl md:text-5xl text-center py-2">
        Join <span class="highlight text-brand-blue">10,000+</span>
      </h2>
      <h2 class="text-3xl md:text-5xl text-center py-2">agents and landlords</h2>
      <p class="text-center py-3 hidden md:block">
        With over 170,000+ applications processed, streamlined tenant screening is just a few steps
        away
      </p>

      <p class="text-brand-blue uppercase py-3 md:py-5 text-center">Trusted by</p>

      <img src="@/assets/images/signup-trusted-by.png" />

      <div class="hidden md:flex flex-col">
        <p class="text-brand-blue uppercase mt-8 text-center">Included</p>

        <div class="flex items-center py-3">
          <CheckIcon class="text-brand-green w-8 h-8 mr-3" />
          <p>Free - No subscriptions or hidden fees</p>
        </div>
        <div class="flex items-center py-3">
          <CheckIcon class="text-brand-green w-8 h-8 mr-3" />
          <p>Designed for agent & landlord collaboration</p>
        </div>

        <div class="flex py-3">
          <div class="flex mr-3">
            <CheckIcon class="text-brand-green w-8 h-8" />
          </div>
          <p class="">
            The full range of reports and verifications. Reports: credit, criminal, & eviction
            Verifications: employment, income, & landlord
          </p>
        </div>

        <p class="py-5 font-semibold text-center">+ much more</p>
      </div>
    </div>

    <div class="w-full md:w-1/2 flex flex-row bg-white rounded mx-auto">
      <div class="flex w-full flex-col px-5 py-5 md:py-10 md:px-10">
        <div class="flex flex-col pb-5">
          <label class="pb-1">Name</label>
          <input
            v-model="name"
            type="text"
            class="py-2 w-full px-4 border-1 border-brand-mid-gray shadow-sm rounded-sm placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green"
          />
        </div>

        <div class="flex flex-col pb-5">
          <label class="pb-1"
            >I am using Rent Application as a(n) <span class="text-brand-red">*</span></label
          >
          <DropDown
            :options="userTypeOptions"
            placeholder="Please choose one"
            @select="selectUserType"
          ></DropDown>

          <p class="text-sm text-brand-mid-gray pt-2">
            Are you an applicant trying to apply to a property?
          </p>
          <div>
            <a
              href="https://rent-application-faq.groovehq.com/help/i-am-an-applicant-how-do-i-apply"
              target="_blank"
              class="text-sm text-brand-green"
              >Click here for assistance</a
            >
          </div>
        </div>

        <div class="flex flex-col pb-5">
          <label class="pb-1"
            >Company Name <span class="text-brand-mid-gray">(optional)</span></label
          >
          <input
            v-model="companyName"
            type="text"
            class="py-2 w-full px-4 border-1 border-brand-mid-gray shadow-sm rounded-sm placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green"
          />
        </div>

        <div class="flex flex-col pb-5">
          <label class="pb-1">Email <span class="text-brand-red">*</span></label>
          <input
            v-model="email"
            type="email"
            class="py-2 w-full px-4 border-1 border-brand-mid-gray shadow-sm rounded-sm placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green"
          />
        </div>

        <div class="flex flex-col pb-5">
          <label class="pb-1">Password <span class="text-brand-red">*</span></label>
          <input
            v-model="password"
            type="password"
            class="py-2 w-full px-4 border-1 border-brand-mid-gray shadow-sm rounded-sm placeholder-brand-mid-gray focus:border-brand-green focus:ring-brand-green"
          />
        </div>

        <PasswordGuide :password="password" ref="passwordGuide" />

        <div class="flex flex-row py-5">
          <div class="flex items-center">
            <input
              id="agreeToTerms"
              v-model="agreeToTerms"
              type="checkbox"
              class="rounded border-gray-300 text-brand-green focus:ring-brand-green w-5 h-5"
            />
          </div>
          <label for="agreeToTerms" class="text-sm md:text-base ml-2 block text-brand-black"
            >I agree to Rent Application's
            <router-link class="text-brand-green" :to="{ name: 'home_terms' }"
              >Terms of Use</router-link
            ></label
          >
        </div>

        <!-- START Error Message -->
        <div class="flex flex-col pb-5" v-if="getUserError">
          <div
            v-if="errorMessage"
            class="rounded border border-brand-red mx-auto py-2 px-3 w-full"
            style="background: #f8dade"
          >
            <p class="text-brand-red text-sm py-1">
              {{ errorMessage }}
            </p>
          </div>
        </div>
        <!-- END Error Message -->

        <div class="flex flex-col py-5">
          <PrimaryButton
            @click="signup"
            :disabled="!validInput || isUserLoading"
            :is-loading="isUserLoading"
            >Create Account
          </PrimaryButton>
        </div>
      </div>
    </div>
  </div>
  <Footer />
</template>
<script>
import { XIcon, CheckIcon } from "@heroicons/vue/outline"

// eslint-disable-next-line no-unused-vars
import { USER_REGISTER, AFFILIATE_CHECK_NAME } from "@/store/types"
import { mapGetters } from "vuex"
import moment from "moment"
import _ from "lodash"

export default {
  components: {
    XIcon,
    CheckIcon
  },
  computed: {
    ...mapGetters(["getUserError", "getCurrentReferrer", "getCurrentUser", "isUserLoading"]),
    hasReferrer() {
      return !!this.$route.params.referrer
    },
    validInput() {
      return (
        !!this.userType &&
        !!this.email &&
        !!this.password &&
        this.$refs.passwordGuide.isValid &&
        !!this.agreeToTerms
      )
    }
  },
  async mounted() {
    this.applyAppSlug = localStorage.getItem("apply_app_slug")

    if (this.applyAppSlug) {
      localStorage.removeItem("apply_app_slug")
    }
  },
  data() {
    return {
      applyAppSlug: null,
      userTypeOptions: [
        {
          value: "brokerage",
          label: "Agency / Brokerage or Real Estate Professional"
        },
        { value: "property_manager", label: "Property Manager" },
        { value: "landlord", label: "Independent Landlord" }
      ],
      name: "",
      userType: "",
      companyName: "",
      email: "",
      password: "",
      agreeToTerms: false,
      showReferrerToast: false,
      errorMessage: ""
    }
  },
  methods: {
    async loadReferrer(name) {
      await this.$store.dispatch(AFFILIATE_CHECK_NAME, name)

      if (this.getCurrentReferrer && !localStorage.getItem("ambassador")) {
        const data = _.cloneDeep(this.getCurrentReferrer)
        data.expire_at = moment().add(30, "days")
        localStorage.setItem("ambassador", JSON.stringify(data))
      }

      this.showReferrerToast = !!this.getCurrentReferrer
    },
    async signup() {
      if (!this.validInput) {
        return
      }
      const { name, userType, companyName, email, password } = this

      try {
        await this.$store.dispatch(USER_REGISTER, {
          name,
          userType,
          companyName,
          email,
          password,
          referrer: this.$route.query.referrer,
          referrerData: {
            ORIGINAL_HTTP_REFERER: this.$cookie.getCookie("ORIGINAL_HTTP_REFERER"),
            ORIGINAL_ENTRY_PAGE: this.$cookie.getCookie("ORIGINAL_ENTRY_PAGE")
          }
        })
        localStorage.setItem("just_signed_up", true)

        this.$router.push({ name: "dashboard_forms" })
      } catch (err) {
        const error = err.response
        //Get first error
        this.errorMessage = Object.values(error.data).flat()[0]
      }
    },
    closeReferrerToast() {
      this.showReferrerToast = false
    },
    selectUserType(selected) {
      this.userType = selected.value
    }
  }
}
</script>
